import React from 'react';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import SEO from '../components/common/SEO';
import { SITE_URL } from '../utils/env';

const copy = {
  ko: {
    title: '포스트팝 계정 및 데이터 삭제',
    intro: '앱을 다시 설치하지 않아도 계정 삭제를 요청할 수 있습니다.',
    request: '이메일로 삭제 요청',
    steps:
      '제목에 “포스트팝 계정 삭제 요청”을 적고, 가입 이메일·로그인 방식·닉네임을 알려주세요. 계정 소유 여부를 확인한 뒤 삭제 절차를 안내합니다.',
    safety:
      '비밀번호나 인증번호는 보내지 마세요. 이메일을 보내는 것만으로 즉시 계정이 삭제되지는 않습니다.',
    subject: '포스트팝 계정 삭제 요청',
    owner: '접수 담당: 김민형 · 포스트팝 운영',
    directTitle: '직접 탈퇴하기',
    app: '앱: MY → 설정 → 회원 탈퇴',
    web: '웹: 로그인 → MY → 설정 → 회원 탈퇴',
    my: '웹 MY로 이동',
    dataTitle: '삭제되는 정보와 별도 보관 기록',
    data: '회원 탈퇴는 계정 정보와 리뷰·댓글·평가 등 연결된 활동 기록의 삭제 또는 익명화를 포함합니다. 처리된 계정은 복구할 수 없습니다.',
    retained:
      '결제·환불 기록 등 별도 보관되는 정보와 보유기간은 개인정보 처리방침을 확인해 주세요. 삭제 요청 처리 시 적용 범위와 남는 기록을 함께 안내합니다.',
    subscription:
      '계정 삭제와 정기후원 구독 해지는 별개입니다. 정기후원을 이용 중이라면 구매한 스토어의 구독 설정에서 자동 갱신을 확인해 주세요. 구독 중에도 계정 삭제를 요청할 수 있습니다.',
    policy: '개인정보 처리방침',
    contact: '일반 문의',
  },
  en: {
    title: 'Delete your Postpop account and data',
    intro: 'You can request account deletion without reinstalling the app.',
    request: 'Request deletion by email',
    steps:
      'Use the subject “Postpop account deletion request” and include your sign-in email, sign-in method, and nickname. We will verify account ownership and guide you through deletion.',
    safety:
      'Do not send passwords or verification codes. Sending an email does not immediately delete your account.',
    subject: 'Postpop account deletion request',
    owner: 'Contact: Min Hyung Kim · Postpop Operations',
    directTitle: 'Delete your account yourself',
    app: 'App: MY → Settings → Delete account',
    web: 'Website: Sign in → MY → Settings → Delete account (회원 탈퇴)',
    my: 'Open MY on the website',
    dataTitle: 'Deleted information and retained records',
    data: 'Account deletion includes deleting or anonymizing account information and associated activity such as reviews, comments, and ratings. A deleted account cannot be restored.',
    retained:
      'See our privacy policy for information retained separately, such as payment and refund records, and its retention periods. We will explain the scope of deletion and any retained records when handling your request.',
    subscription:
      'Deleting your account and canceling monthly support are separate actions. If you have a subscription, check automatic renewal in the subscription settings of the store where you purchased it. You can request account deletion while subscribed.',
    policy: 'Privacy policy',
    contact: 'General support',
  },
};

export default function AccountDeletion() {
  const [params, setParams] = useSearchParams();
  const language = params.get('lang') === 'en' ? 'en' : 'ko';
  const text = copy[language];
  return (
    <PageLayout maxWidth="780px">
      <SEO title={text.title} description={text.intro} url={`${SITE_URL}/account-deletion`} />
      <Stack
        spacing={3}
        lang={language}
        sx={{
          overflowWrap: 'anywhere',
          '& p': { lineHeight: 1.65 },
          '& h2': { fontSize: '18px', fontWeight: 700, lineHeight: 1.4 },
        }}
      >
        <Stack direction="row" spacing={1} aria-label="Language">
          {['ko', 'en'].map((locale) => (
            <Button
              key={locale}
              variant={locale === language ? 'contained' : 'outlined'}
              aria-pressed={locale === language}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set('lang', locale);
                setParams(next, { replace: true });
              }}
            >
              {locale === 'ko' ? '한국어' : 'English'}
            </Button>
          ))}
        </Stack>
        <Box>
          <Typography
            component="h1"
            sx={{ fontSize: { xs: '24px', md: '28px' }, fontWeight: 700, lineHeight: 1.4 }}
            gutterBottom
          >
            {text.title}
          </Typography>
          <Typography>{text.intro}</Typography>
        </Box>
        <Stack spacing={1.5}>
          <Typography component="h2" variant="h6">
            {text.request}
          </Typography>
          <Typography>{text.steps}</Typography>
          <Link
            href={`mailto:contact@postpop.kr?subject=${encodeURIComponent(text.subject)}`}
            sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
          >
            contact@postpop.kr
          </Link>
          <Typography color="text.secondary">{text.owner}</Typography>
          <Typography>{text.safety}</Typography>
        </Stack>
        <Stack spacing={1.5}>
          <Typography component="h2" variant="h6">
            {text.directTitle}
          </Typography>
          <Typography>{text.app}</Typography>
          <Typography>{text.web}</Typography>
          <Link href="/mypage/settings" sx={{ alignSelf: 'flex-start' }}>
            {text.my}
          </Link>
        </Stack>
        <Stack spacing={1.5}>
          <Typography component="h2" variant="h6">
            {text.dataTitle}
          </Typography>
          <Typography>{text.data}</Typography>
          <Typography>{text.retained}</Typography>
          <Typography>{text.subscription}</Typography>
        </Stack>
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={3}>
          <Link href="/about/privacy-policy">{text.policy}</Link>
          <Link href="/contact">{text.contact}</Link>
        </Stack>
      </Stack>
    </PageLayout>
  );
}
